<template>
  <div class="player">
    <button class="button_play" @click="handlePlayBtnClick">
      <img :src="btnIcon" class="w-4 h-4" alt="" />
    </button>

    <div class="progressBar">
      <div ref="barRef" class="handle-bg w-full cursor-pointer" @click="handleSetPosition">
        <div class="handle-bg bg-primary" :style="`width: ${progress || 0}%`">
          <div class="handle" @touchstart="onPointStart" @mousedown="onPointStart"></div>
        </div>
      </div>
    </div>

    <audio
      ref="audio"
      :src="fileUrl"
      @canplaythrough="handleCanplaythrough"
      @timeupdate="handelTimeupdate"
      @pause="handlePlayerStateChange"
      @play="handlePlayerStateChange"
      @error="error"
    ></audio>

    <p class="w-24">{{ formatTime(currentTime) }}/{{ getDurMMSS }}</p>
  </div>
</template>
<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import IconPlay from './assets/play-icon.svg';
import IconPause from './assets/pause-icon.svg';

const moveEventList = ['touchmove', 'mousemove'];
const endEventList = ['touchend', 'mouseup'];

@Component({})
export default class AudioRecordPlayer extends Vue {
  @Prop() fileUrl!: string;
  duration = 0;

  currentTime = 0;
  paused = true;
  audioError = false;

  dragging = false;
  readyPlay = false;
  hasLoading = true;

  touchInfo = {
    startX: 0,
    offsetLeft: 0
  };

  get btnIcon() {
    return this.paused ? IconPlay : IconPause;
  }

  get progress() {
    return Math.floor((this.currentTime / this.duration) * 10000) / 100;
  }

  get getDurMMSS() {
    return this.formatTime(this.duration);
  }

  formatTime(time: number) {
    const seconds = Math.floor(time % 60);
    const minutes = Math.floor((time - seconds) / 60);
    if (isNaN(seconds) || isNaN(minutes)) return '--:--';
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  async audioCanplay(e: Event) {
    const firsthandAudio = e.target as HTMLAudioElement;
    this.duration = firsthandAudio.duration;
  }

  handelOnLoad(e: Event) {
    const audio = e.target as HTMLAudioElement;
    this.duration = audio.duration;
    this.hasLoading = false;
  }

  async handleCanplaythrough(e: Event) {
    const firsthandAudio = e.target as HTMLAudioElement;
    // NOTE 解决NaN和infinity的问题，录完点播放不能马上播，会有1s的延迟
    while (firsthandAudio.duration === Infinity) {
      this.readyPlay = false;

      // eslint-disable-next-line no-promise-executor-return
      await new Promise((r) => setTimeout(r, 200));
      firsthandAudio.currentTime = 10000 * Math.random();
    }

    this.duration = firsthandAudio.duration;
    setTimeout(() => {
      this.readyPlay = true;
    }, 200);
  }

  handelTimeupdate(e: Event) {
    if (!this.readyPlay || this.dragging) return;
    const audio = e.target as HTMLAudioElement;
    this.currentTime = (e.target as HTMLAudioElement).currentTime;
    this.duration = audio.duration;
  }

  error() {
    this.hasLoading = false;
    this.audioError = true;
  }

  handlePlayBtnClick() {
    const audio = this.$refs.audio as HTMLAudioElement;
    if (this.paused) {
      if (audio.currentTime === audio.duration) {
        audio.currentTime = 0;
      }
      audio.play();
    } else {
      audio.pause();
    }
  }

  handlePlayerStateChange(e: Event) {
    this.paused = (e.target as HTMLAudioElement).paused;
  }

  onPointStart(e: TouchEvent | MouseEvent) {
    const startX = (e as TouchEvent).touches?.[0].pageX || (e as MouseEvent).pageX;
    if (!this.readyPlay) return;

    this.dragging = true;

    this.touchInfo.startX = startX;
    const target = e.target as HTMLElement;
    this.touchInfo.offsetLeft = target.offsetLeft + target.clientWidth / 2;
    moveEventList.forEach((item: any) => window.addEventListener(item, this.onPointMove));
    endEventList.forEach((item: any) => window.addEventListener(item, this.onPointEnd));
  }

  onPointMove(e: TouchEvent | MouseEvent) {
    if (!this.dragging || !this.readyPlay) return;
    console.log('move', this.dragging);
    const barRef = this.$refs.barRef as HTMLElement;
    const audio = this.$refs.audio as HTMLAudioElement;

    const startX = (e as TouchEvent).touches?.[0].pageX || (e as MouseEvent).pageX;
    const distance = startX - this.touchInfo.startX + this.touchInfo.offsetLeft;
    if (barRef?.clientWidth && audio?.duration) {
      let percent = distance / barRef.clientWidth;
      percent = percent > 1 ? 1 : percent < 0 ? 0 : percent;
      this.currentTime = percent * audio?.duration;
    }
  }
  onPointEnd() {
    if (!this.readyPlay) return;
    const audio = this.$refs.audio as HTMLAudioElement;
    moveEventList.forEach((item: any) => window.removeEventListener(item, this.onPointMove));
    endEventList.forEach((item: any) => window.removeEventListener(item, this.onPointEnd));
    this.dragging = false;
    if (audio) {
      audio.currentTime = this.currentTime;
    }
  }

  handleSetPosition(e: PointerEvent) {
    const barRef = this.$refs.barRef as HTMLElement;
    const audio = this.$refs.audio as HTMLAudioElement;
    if (barRef) {
      const percent = e.offsetX / barRef.offsetWidth;

      audio.currentTime = percent * audio?.duration;
    }
  }
}
</script>
<style scoped lang="scss">
.player {
  @apply flex flex-row items-center justify-between w-full px-5 py-3 space-x-6;
  border-width: 1px;
  border-radius: 2.5rem;
}
.button_play {
  @apply hover:opacity-80 flex items-center justify-center w-10 h-10 transition-all rounded-full cursor-pointer;
  background-image: linear-gradient(to right, #524eff, #8b8eff);
}
.progressBar {
  @apply relative flex items-center flex-1 h-2 bg-gray-200 rounded;
}
.progressBar .handle {
  @apply absolute -right-1 w-2 h-8  bg-gray-500 rounded cursor-move;
}

.progressBar .handle-bg {
  @apply absolute left-0 h-full rounded flex items-center;
}

p {
  @apply pointer-events-none;
  font-feature-settings: 'tnum';
}
</style>
